import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  FaDatabase, FaDownload, FaPrint, FaFilter, FaTable, 
  FaCalendarAlt, FaSearch, FaChartPie, FaChartLine, FaCheckCircle, 
  FaStar, FaBriefcase, FaUsers, FaUserCheck, FaFileAlt
} from 'react-icons/fa';

interface EtlRecord {
  id: number | string;
  field_1: string;
  field_2: string;
  field_3: string;
  field_4: string;
  amount?: number;
  rating?: number;
  status?: string;
  date?: string;
  raw_data?: Record<string, any>;
}

const EtlReportStudio: React.FC = () => {
  const [domain, setDomain] = useState<string>('R2R_BUSINESS');
  const [dateRange, setDateRange] = useState<string>('THIS_MONTH');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [records, setRecords] = useState<EtlRecord[]>([]);
  const [summaryStats, setSummaryStats] = useState({
    total_records: 0,
    total_amount: 0,
    avg_rating: 0,
    verified_count: 0
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchEtlData();
  }, [domain, dateRange, statusFilter]);

  const fetchEtlData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/etl/extract`, {
        params: {
          domain,
          dateRange,
          startDate,
          endDate,
          statusFilter
        }
      });
      if (res.data?.success) {
        setRecords(res.data.records || []);
        setSummaryStats(res.data.summary || {
          total_records: 0,
          total_amount: 0,
          avg_rating: 0,
          verified_count: 0
        });
      }
    } catch (err) {
      console.error('Error fetching ETL data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = records.filter(r => {
    const q = searchQuery.toLowerCase();
    return (
      (r.field_1 || '').toLowerCase().includes(q) ||
      (r.field_2 || '').toLowerCase().includes(q) ||
      (r.field_3 || '').toLowerCase().includes(q) ||
      (r.field_4 || '').toLowerCase().includes(q)
    );
  });

  const exportToCsv = () => {
    if (filteredRecords.length === 0) {
      alert('No records available to export.');
      return;
    }

    const headers = getDomainHeaders();
    const csvRows = [headers.join(',')];

    filteredRecords.forEach(r => {
      const row = [
        `"${(r.field_1 || '').replace(/"/g, '""')}"`,
        `"${(r.field_2 || '').replace(/"/g, '""')}"`,
        `"${(r.field_3 || '').replace(/"/g, '""')}"`,
        `"${(r.field_4 || '').replace(/"/g, '""')}"`,
        r.amount ? r.amount : 0,
        r.rating ? r.rating : '',
        `"${r.status || ''}"`,
        `"${r.date || ''}"`
      ];
      csvRows.push(row.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `RMB_ETL_Report_${domain}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrintReport = () => {
    window.print();
  };

  const getDomainHeaders = () => {
    switch (domain) {
      case 'R2R_BUSINESS':
        return ['Business Giver', 'Receiver Member', 'Referred Person / Topic', 'Details', 'Closed Value (₹)', 'Rating', 'Status', 'Date'];
      case 'MEMBERS':
        return ['Member Name', 'Email & Phone', 'Company Name', 'Classification & Sub-Role', 'Joined Points', 'Rating', 'Status', 'Joined Date'];
      case 'ATTENDANCE':
        return ['Meeting Title', 'Member / Visitor', 'Attendance Status', 'Meeting Venue', 'Points', 'Rating', 'Status', 'Meeting Date'];
      case 'VISITORS':
        return ['Visitor Full Name', 'Contact & Email', 'Company Name', 'Form Type', 'Fee Amount (₹)', 'Rating', 'Status', 'Submitted Date'];
      case 'TESTIMONIALS':
        return ['Member / Author', 'Designation', 'Testimonial Content', 'Media Attachment', 'Score', 'Rating (1-5 ★)', 'Status', 'Created Date'];
      default:
        return ['Col 1', 'Col 2', 'Col 3', 'Col 4', 'Amount', 'Rating', 'Status', 'Date'];
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-[#131E32] min-h-screen text-slate-100 space-y-6">
      
      {/* Header Banner */}
      <div className="bg-[#1E2B45] p-4 sm:p-6 rounded-2xl border border-[#2A3B5C] flex flex-col md:flex-row justify-between items-start md:items-center gap-4 shadow-xl">
        <div>
          <span className="bg-[#FDBC12] text-[#0A2451] text-[10px] font-black uppercase px-3 py-1 rounded-full tracking-wider shadow">
            ⚡ ETL Engine v2.0
          </span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-2 flex items-center gap-2">
            <FaDatabase className="text-[#FDBC12]" /> ETL Reports &amp; Data Transposition Studio
          </h1>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl">
            Extract raw operational data from RMB database tables, transpose layouts by domain, calculate aggregations, and load/export formatted CSV &amp; PDF reports.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
          <button
            onClick={exportToCsv}
            className="flex-1 md:flex-initial px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black rounded-xl shadow-lg transition cursor-pointer flex items-center justify-center gap-2"
          >
            <FaDownload /> Export CSV / Excel
          </button>
          <button
            onClick={handlePrintReport}
            className="flex-1 md:flex-initial px-4 py-2.5 bg-[#0A2451] hover:bg-blue-900 border border-[#2A3B5C] text-xs font-bold text-white rounded-xl transition cursor-pointer flex items-center justify-center gap-2"
          >
            <FaPrint /> Print Summary
          </button>
        </div>
      </div>

      {/* Domain Selection Tabs (EXTRACT MODE) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { key: 'R2R_BUSINESS', label: 'R2R Deals & Business', icon: <FaBriefcase /> },
          { key: 'MEMBERS', label: 'Members & Roster', icon: <FaUsers /> },
          { key: 'ATTENDANCE', label: 'Attendance & Meetings', icon: <FaUserCheck /> },
          { key: 'VISITORS', label: 'Visitor Enquiries', icon: <FaFileAlt /> },
          { key: 'TESTIMONIALS', label: 'Testimonials & Ratings', icon: <FaStar /> }
        ].map((d) => (
          <button
            key={d.key}
            onClick={() => setDomain(d.key)}
            className={`p-3.5 rounded-2xl border text-xs font-extrabold flex flex-col items-center justify-center gap-2 transition cursor-pointer shadow-md ${
              domain === d.key
                ? 'bg-[#FDBC12] text-[#0A2451] border-[#FDBC12] ring-2 ring-amber-400/40'
                : 'bg-[#1E2B45] text-slate-300 border-[#2A3B5C] hover:bg-[#253554] hover:text-white'
            }`}
          >
            <span className="text-lg">{d.icon}</span>
            <span className="text-center">{d.label}</span>
          </button>
        ))}
      </div>

      {/* Transpose & Transformation Toolbar */}
      <div className="bg-[#1E2B45] p-4 sm:p-5 rounded-2xl border border-[#2A3B5C] space-y-4 shadow-lg">
        <div className="flex items-center gap-2 text-xs font-extrabold text-amber-300 uppercase tracking-wider">
          <FaFilter /> Transpose &amp; Filter Options
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
          {/* Date Range Preset */}
          <div>
            <label className="font-bold text-slate-300 block mb-1">Time Period</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none cursor-pointer"
            >
              <option value="ALL_TIME">All Time</option>
              <option value="THIS_MONTH">This Month</option>
              <option value="LAST_MONTH">Last Month</option>
              <option value="LAST_3_MONTHS">Last 3 Months</option>
              <option value="FINANCIAL_YEAR">Current Society Year</option>
              <option value="CUSTOM">Custom Date Range</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="font-bold text-slate-300 block mb-1">Verification Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full p-2.5 bg-[#131E32] border border-[#2A3B5C] rounded-xl text-white font-bold outline-none cursor-pointer"
            >
              <option value="ALL">All Statuses</option>
              <option value="VERIFIED">Verified / Approved Only</option>
              <option value="PENDING">Pending Verification Only</option>
              <option value="REJECTED">Rejected Only</option>
            </select>
          </div>

          {/* Search Query Input */}
          <div className="sm:col-span-2">
            <label className="font-bold text-slate-300 block mb-1">Quick Search Records</label>
            <div className="relative">
              <FaSearch className="absolute left-3.5 top-3.5 text-slate-400 text-xs" />
              <input
                type="text"
                placeholder="Search member, company, deal topic, or notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-[#131E32] border border-[#2A3B5C] focus:border-[#FDBC12] rounded-xl text-white outline-none font-bold text-xs"
              />
            </div>
          </div>
        </div>

        {dateRange === 'CUSTOM' && (
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#2A3B5C]">
            <div>
              <label className="font-bold text-slate-300 block mb-1 text-xs">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-2 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white text-xs outline-none"
              />
            </div>
            <div>
              <label className="font-bold text-slate-300 block mb-1 text-xs">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-2 bg-[#131E32] border border-[#2A3B5C] rounded-lg text-white text-xs outline-none"
              />
            </div>
          </div>
        )}
      </div>

      {/* KPI Transposed Aggregation Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#1E2B45] p-4 sm:p-5 rounded-2xl border border-[#2A3B5C] shadow-lg flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400 text-xl font-black">
            <FaTable />
          </div>
          <div>
            <div className="text-xs text-slate-400 font-bold uppercase">Extracted Records</div>
            <div className="text-2xl font-black text-white">{filteredRecords.length}</div>
          </div>
        </div>

        <div className="bg-[#1E2B45] p-4 sm:p-5 rounded-2xl border border-emerald-500/30 shadow-lg flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 text-xl font-black">
            <FaChartLine />
          </div>
          <div>
            <div className="text-xs text-emerald-400 font-bold uppercase">Total Aggregated Value</div>
            <div className="text-2xl font-black text-emerald-400">
              ₹{Number(summaryStats.total_amount || 0).toLocaleString()}
            </div>
          </div>
        </div>

        <div className="bg-[#1E2B45] p-4 sm:p-5 rounded-2xl border border-amber-500/30 shadow-lg flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 text-xl font-black">
            <FaStar />
          </div>
          <div>
            <div className="text-xs text-amber-400 font-bold uppercase">Average Score / Rating</div>
            <div className="text-2xl font-black text-amber-400 flex items-center gap-1">
              <span>{summaryStats.avg_rating || '5.0'}</span>
              <span className="text-xs text-amber-300">★ ★ ★ ★ ★</span>
            </div>
          </div>
        </div>

        <div className="bg-[#1E2B45] p-4 sm:p-5 rounded-2xl border border-purple-500/30 shadow-lg flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400 text-xl font-black">
            <FaCheckCircle />
          </div>
          <div>
            <div className="text-xs text-purple-400 font-bold uppercase">Verified Ratio</div>
            <div className="text-2xl font-black text-purple-300">
              {summaryStats.verified_count || 0} / {summaryStats.total_records || 0}
            </div>
          </div>
        </div>
      </div>

      {/* Extracted & Transposed Data Grid */}
      <div className="bg-[#1E2B45] rounded-2xl border border-[#2A3B5C] overflow-hidden shadow-xl">
        <div className="p-4 border-b border-[#2A3B5C] bg-[#131E32] flex flex-wrap justify-between items-center gap-2">
          <h3 className="font-bold text-white text-sm flex items-center gap-2">
            <FaDatabase className="text-[#FDBC12]" /> Loaded Data Matrix ({filteredRecords.length} entries)
          </h3>
          <span className="text-xs text-slate-400">
            Domain: <strong className="text-amber-400">{domain}</strong>
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead className="bg-[#0B1220] text-slate-300 font-bold border-b border-[#2A3B5C] uppercase">
              <tr>
                {getDomainHeaders().map((h, idx) => (
                  <th key={idx} className="p-3.5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2A3B5C] text-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-400 font-medium">Extracting and transposing data...</td>
                </tr>
              ) : filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-400 font-medium">No ETL records matched selected parameters.</td>
                </tr>
              ) : (
                filteredRecords.map((r, idx) => (
                  <tr key={r.id || idx} className="hover:bg-[#131E32] transition">
                    <td className="p-3.5 font-bold text-amber-400">{r.field_1 || '-'}</td>
                    <td className="p-3.5 text-blue-300 font-semibold">{r.field_2 || '-'}</td>
                    <td className="p-3.5 text-white max-w-xs">{r.field_3 || '-'}</td>
                    <td className="p-3.5 text-slate-300 max-w-xs truncate">{r.field_4 || '-'}</td>
                    <td className="p-3.5 font-black text-emerald-400 text-sm">
                      {r.amount ? `₹${Number(r.amount).toLocaleString()}` : '-'}
                    </td>
                    <td className="p-3.5">
                      {r.rating ? (
                        <div className="flex items-center gap-1 text-amber-400 font-bold">
                          <FaStar /> <span>{r.rating}/5</span>
                        </div>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td className="p-3.5">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase ${
                        r.status === 'VERIFIED' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' :
                        r.status === 'REJECTED' ? 'bg-rose-500/10 text-rose-400 border border-rose-500/30' :
                        'bg-amber-500/10 text-amber-400 border border-amber-500/30'
                      }`}>
                        {r.status || 'Active'}
                      </span>
                    </td>
                    <td className="p-3.5 text-slate-400">{r.date ? new Date(r.date).toLocaleDateString() : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EtlReportStudio;
